import { useEffect, useRef } from 'react';
import type React from 'react';
import type { SuggestInputProps } from './suggestInput';

type UseSuggestInputArgs = Pick<
  SuggestInputProps,
  'name' | 'onChange' | 'index' | 'id' | 'maxLength' | 'onKeyDown'
>;

const useSuggestInput = ({
  name,
  onChange = () => {},
  index,
  id,
  maxLength,
  onKeyDown = () => {},
}: UseSuggestInputArgs) => {
  const inputElem = useRef<any>(null);

  /**
   * Relays typed text from a suggest input or textarea to the owning form. The value is read off the event target and sent with the field name and row index.
   *
   * @param e - Change event from the input or textarea.
   * @returns Nothing.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    onChange(name, value, index);
  };

  /**
   * Splices a suggested snippet into the input at the caret, as used for report-text suggestions. The element is looked up by id, insertion is abandoned when the result would exceed maxLength, otherwise the text is spliced at the caret, focus and the caret are restored after the inserted run, and the new value is reported to the form.
   *
   * @param text - The snippet to insert at the caret.
   * @returns Nothing.
   */
  const insertText = (text: string) => {
    // get current cursor position
    if (id) {
      const input = document.getElementById(id) as HTMLInputElement;
      if (input) {
        // check maxLength
        if (maxLength && input.value.length + text.length > maxLength) {
          input.focus();
          return;
        }
        const cursorPosition = input.selectionStart as number;
        input.value =
          input.value.slice(0, cursorPosition) + text + input.value.slice(cursorPosition);
        // check maxLength in case could replace last text
        // if (maxLength && input.value.length > maxLength) {
        //   input.value = input.value.slice(0, maxLength);
        // }
        input.focus();
        input.setSelectionRange(cursorPosition + text.length, cursorPosition + text.length);
        onChange(name, input.value, index);
      }
    }
  };

  useEffect(() => {
    if (id) {
      const input = document.getElementById(id);
      if (input) {
        input.addEventListener('keydown', (event: any) => {
          event.stopPropagation();
          if (event.keyCode === 13 || event.code === 'Enter') {
            if (onKeyDown) {
              onKeyDown(event);
            }
          }
        });
      }
    }
  }, [id]);

  return { inputElem, handleChange, insertText };
};

export default useSuggestInput;
